import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../utils/FirebaseConfig';
import { syncEtikafAccount } from '../utils/accountRoles';
import '../styles/Form.css';
import { FaArrowLeft, FaEye, FaEyeSlash } from 'react-icons/fa';

const REGISTRATION_STATUS_API = 'https://us-central1-etikaf-admin.cloudfunctions.net/getEtikafRegistrationStatus';
const EMAIL_ACTION_SETTINGS = {
  url: 'https://etikaf-admin.firebaseapp.com/daftar?verified=1&intent=etikaf-my',
  handleCodeInApp: false,
};

const Login = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMode, setResetMode] = useState(false);

  const currentYear = new Date().getFullYear();

  const login = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      console.log("✅ Authenticated:", user.email);

      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        console.log("ℹ️ Akaun belum aktif. Menyemak pendaftaran...");
        if (!user.emailVerified) {
          await sendEmailVerification(user, EMAIL_ACTION_SETTINGS);
          setMessage('Akaun belum aktif. Pautan pengesahan e-mel telah dihantar semula.');
          await auth.signOut();
          return;
        }
        await checkPendingRegistration(user);
        return;
      }

      const userData = userDoc.data();
      console.log("📄 Firestore Data Retrieved:", userData);

      syncEtikafAccount(user).catch((syncError) => {
        console.warn("Peranan akaun belum dapat diselaraskan:", syncError);
      });

      if (!userData.approved) {
        console.log("⚠️ Account awaiting approval.");
        setError('Akaun anda masih menunggu pengaktifan.');
        await auth.signOut();
        return;
      }

      console.log("✅ Approved user. Redirecting...");

      const subscriptionEnd = userData.subscriptionEnd || null;

      if (!subscriptionEnd) {
        console.warn("⚠️ Firestore document exists, but subscriptionEnd is missing!");
      } else {
        console.log("📅 subscriptionEnd found:", subscriptionEnd);
      }

      const userProfile = {
        uid: user.uid,
        mosqueId: userData.mosqueId || user.uid,
        namaMasjid: userData.namaMasjid,
        emailMasjid: userData.emailMasjid,
        noTelAnda: userData.noTelAnda || "",
        etvUser: userData.etvUser || false,
        subscriptionEnd: userData.subscriptionEnd || null,
        approved: userData.approved,
        status: userData.status || "active",
      };

      localStorage.setItem("userProfile", JSON.stringify(userProfile));
      localStorage.setItem("isAuthenticated", "true");

      setIsAuthenticated(true);
      navigate('/');
    } catch (authError) {
      console.error("❌ Auth login failed:", authError);

      if (
        [
          "auth/wrong-password",
          "auth/invalid-email",
          "auth/user-not-found",
          "auth/invalid-credential",
        ].includes(authError.code)
      ) {
        setError("Emel atau kata laluan tidak tepat.");
        return;
      }

      setError('Log masuk tidak berjaya. Cuba semula.');
    }

    console.log("🔄 Login successful. Redirecting...");
  };

  const openResetMode = () => {
    setResetMode(true);
    setError('');
    setMessage('');
  };

  const closeResetMode = () => {
    setResetMode(false);
    setError('');
    setMessage('');
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    const cleanEmail = email.trim().toLowerCase();

    if (!cleanEmail) {
      setError('Masukkan alamat emel anda.');
      return;
    }

    try {
      setResetLoading(true);
      setEmail(cleanEmail);
      await sendPasswordResetEmail(auth, cleanEmail);
      setMessage(`Pautan reset telah dihantar ke ${cleanEmail}. Semak Inbox dan Spam.`);
    } catch (resetError) {
      console.error("❌ Reset password failed:", resetError);

      if (resetError.code === 'auth/user-not-found') {
        setError('Emel tidak ditemui.');
      } else if (resetError.code === 'auth/invalid-email') {
        setError('Alamat emel tidak sah.');
      } else {
        setError('Pautan tidak dapat dihantar. Cuba semula.');
      }
    } finally {
      setResetLoading(false);
    }
  };

  const checkPendingRegistration = async (user) => {
    try {
      const idToken = await user.getIdToken(true);
      const response = await fetch(REGISTRATION_STATUS_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || 'Status pendaftaran tidak dapat disemak.');

      if (data.state === 'pending') {
        const pending = {
          id: data.submitterId || '',
          source: 'secure_status',
          status: data.status || 'pending_payment',
          billUrl: data.billUrl || '',
          namaMasjid: data.namaMasjid || 'Pendaftaran Etikaf',
          emailMasjid: data.emailMasjid || data.email || user.email,
          packageAmount: Number(data.packageAmount || 3600),
          shippingFee: Number(data.shippingFee || 0),
          totalAmount: Number(data.totalAmount || data.packageAmount || 3600),
        };
        localStorage.setItem('pendingRegistration', JSON.stringify(pending));
        localStorage.setItem('submittedEmail', pending.emailMasjid);
        navigate('/status');
        return;
      }

      if (data.state === 'new') {
        navigate('/daftar?verified=1&intent=etikaf-my');
        return;
      }

      navigate('/');
    } catch (submitterError) {
      console.error("❌ Error checking registration:", submitterError);
      setError(submitterError?.message || 'Semakan akaun tidak berjaya. Cuba semula.');
    }
  };

  return (
    <div className="page-container login-page">
      <div className="form-card login-card">
        <div className="loginHeader">
          <a href="https://etikaf.com/my" className='logo-group-login' aria-label="Kembali ke Etikaf">
            <img src="/images/e-icon.png" className="logo-login" alt="Etikaf" />
            <span className="badge-login">Admin</span>
          </a>
        </div>

        <div className="login-intro">
          <span>{resetMode ? 'Pemulihan akaun' : 'Sistem pengurusan masjid'}</span>
          <h1>{resetMode ? 'Tetapkan semula kata laluan' : 'Log masuk Sistem Admin'}</h1>
          <p>
            {resetMode
              ? 'Masukkan emel akaun anda. Kami akan menghantar pautan untuk mencipta kata laluan baharu.'
              : 'Urus maklumat masjid, Etikaf TV dan kandungan jemaah.'}
          </p>
        </div>

        <form onSubmit={resetMode ? handleForgotPassword : login} className="login-form">
          <div className="login-field-group">
            <label htmlFor="email">Emel</label>
            <div className="login-input-wrap">
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="input-field"
                placeholder="nama@masjid.com"
                autoComplete="email"
              />
            </div>
          </div>

          {!resetMode && (
            <>
              <div className="login-field-group">
                <label htmlFor="password">Kata laluan</label>
                <div className="login-input-wrap password-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="input-field"
                    placeholder="Masukkan kata laluan"
                    autoComplete="current-password"
                  />

                  <button
                    type="button"
                    className="toggle-password-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Sembunyikan kata laluan" : "Tunjukkan kata laluan"}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <div className="buttonGroup">
                <button type="submit" className="loginButton login-submit">Log Masuk</button>
                <button
                  type="button"
                  className="forgotPassword resetPasswordBtn"
                  onClick={openResetMode}
                >
                  Lupa kata laluan?
                </button>
              </div>
            </>
          )}

          {resetMode && (
            <div className="password-reset-actions">
              <button type="submit" className="loginButton" disabled={resetLoading}>
                {resetLoading ? 'Menghantar...' : message ? 'Hantar semula pautan' : 'Hantar pautan reset'}
              </button>
              <button type="button" className="password-reset-back" onClick={closeResetMode} disabled={resetLoading}>
                Kembali ke Log Masuk
              </button>
            </div>
          )}

          {error && <p className="error">{error}</p>}
          {message && <p className="success">{message}</p>}
        </form>

        {!resetMode && (
          <div className="new-customer-panel">
            <span>Masjid atau surau belum berdaftar?</span>
            <strong>Mulakan dengan pakej Etikaf</strong>
            <p>Etikaf TV, Sistem Admin 12 bulan dan aplikasi percuma untuk jemaah.</p>
            <a href="/daftar">Lihat pakej &amp; daftar</a>
          </div>
        )}

        <div className='footer-form'>
          <a
            href="https://www.etikaf.com"
            className='go-home'
          >
            <FaArrowLeft /> Kembali ke etikaf.com
          </a>
        </div>
      </div>

      <div className="footer-text">
        &copy; 2017-{currentYear} Etikaf Ent.
      </div>
    </div>
  );
};

export default Login;
